import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import type { TimeRange } from "../components/ui/time-range-selector";
import { trackEvent } from "../lib/analytics";

const MACHINE_STORAGE_KEY = "cc_selected_machine";
const TIME_RANGE_STORAGE_KEY = "cc_time_range";

interface AppContextValue {
  selectedMachine: string | null;
  setSelectedMachine: (code: string) => void;
  timeRange: TimeRange;
  setTimeRange: (range: TimeRange) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [selectedMachine, setSelectedMachineState] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(MACHINE_STORAGE_KEY);
    }
    return null;
  });

  const [timeRange, setTimeRangeState] = useState<TimeRange>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(TIME_RANGE_STORAGE_KEY);
      if (stored && ["1h", "24h", "7d", "14d", "30d", "all"].includes(stored)) {
        return stored as TimeRange;
      }
    }
    return "24h";
  });

  const setSelectedMachine = (code: string) => {
    setSelectedMachineState(code);
    localStorage.setItem(MACHINE_STORAGE_KEY, code);

    // Track filter change with unified event
    trackEvent("filter-change", {
      machine: code,
      timeRange: timeRange,
    });
  };

  const setTimeRange = (range: TimeRange) => {
    setTimeRangeState(range);
    localStorage.setItem(TIME_RANGE_STORAGE_KEY, range);

    // Track filter change with unified event
    trackEvent("filter-change", {
      machine: selectedMachine,
      timeRange: range,
    });
  };

  // Sync with localStorage on mount
  useEffect(() => {
    const storedMachine = localStorage.getItem(MACHINE_STORAGE_KEY);
    if (storedMachine && !selectedMachine) {
      setSelectedMachineState(storedMachine);
    }
  }, [selectedMachine]);

  return (
    <AppContext.Provider
      value={{
        selectedMachine,
        setSelectedMachine,
        timeRange,
        setTimeRange,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
